import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, api, unwrap } from "../api";
import { Button, ErrorNote, Field, input } from "../ui";

/**
 * Provisioning a tenant. Only ever rendered for a super user, because that is
 * the only account the API will accept a company from.
 *
 * The alternative was "run pnpm db:seed", which is not an instruction you can
 * follow from a browser — a fresh deployment was unusable until someone opened
 * a terminal with a production connection string.
 */
function slugify(name: string) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

export function NewCompany({
  email,
  onCreated,
  onCancel,
  footer,
}: {
  email: string;
  onCreated: (company: { slug: string }) => void;
  /** Absent when there is nowhere to go back to — the operator's first company. */
  // `| undefined` is explicit because exactOptionalPropertyTypes is on:
  // callers pass onCancel={maybeUndefined} rather than omitting the prop.
  onCancel?: (() => void) | undefined;
  footer?: React.ReactNode | undefined;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  // Once it's been typed in by hand, stop overwriting it from the name.
  const [slugTouched, setSlugTouched] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [firstClientName, setFirstClientName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(name);

  const create = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.api.companies.$post({
          json: {
            name,
            slug: effectiveSlug,
            baseCurrency: currency,
            ...(firstClientName ? { firstClientName } : {}),
          } as never,
        }),
      ),
    onMutate: () => setError(null),
    onSuccess: (data) => data.company && onCreated(data.company),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Couldn't create that company."),
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-6">
      <div className="w-full max-w-md">
        <h1 className="text-xl font-semibold tracking-tight">New company</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Signed in as {email}. Creating a company sets up a new tenant — its books are separate
          from every other one.
        </p>

        <form
          className="mt-6 flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          {error && <ErrorNote>{error}</ErrorNote>}

          <Field label="Company name">
            <input
              className={input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Construction"
              required
            />
          </Field>

          <Field label="Address" hint={`app.dantria.com/c/${effectiveSlug || "…"}`}>
            <input
              className={input}
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              pattern="[a-z0-9][a-z0-9\-]*[a-z0-9]"
              required
            />
          </Field>

          <Field label="Currency" hint="The currency your books are kept in">
            <input
              className={input}
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              maxLength={3}
              required
            />
          </Field>

          <Field label="First client" hint="Optional — you can add clients later">
            <input
              className={input}
              value={firstClientName}
              onChange={(e) => setFirstClientName(e.target.value)}
              placeholder="Northwind Retail"
            />
          </Field>

          <div className="mt-1 flex items-center gap-3">
            <Button type="submit" disabled={create.isPending || !name}>
              {create.isPending ? "Creating…" : "Create company"}
            </Button>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="text-sm text-neutral-600 underline underline-offset-4 hover:text-neutral-900"
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        <p className="mt-4 text-xs text-neutral-500">
          It starts with no members — invite the owner from Settings once it exists. A starting set
          of cost codes and document numbering is created with it, so the first expense and the
          first invoice both have somewhere to go.
        </p>

        {footer}
      </div>
    </div>
  );
}
